/**
 * Dashboard UI strings: the source catalog. Every other locale must have every key (the
 * `Catalog` type enforces it). `{name}` is a placeholder; a key used with a numeric `count`
 * may have `.one` / `.other` forms (CLDR plural categories).
 */
export const en = {
  "common.retry": "Retry",
  "common.reload": "Reload",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.saved": "Saved",
  "common.saveFailed": "Save failed",
  "common.saveChanges": "Save changes",
  "common.delete": "Delete",
  "common.remove": "Remove",
  "common.cancel": "Cancel",
  "common.edit": "Edit",
  "common.openLink": "Open",
  "common.back": "← Back",
  "common.loading": "Loading",
  "common.loadingMore": "Loading…",
  "common.sending": "Sending…",
  "common.error": "Something went wrong",
  "common.failed": "Failed",
  "common.name": "Name",
  "common.email": "Email",
  "common.title": "Title",
  "common.language": "Language",

  "time.now": "now",
  "time.lessThanMinute": "<1m",
  "time.minutes": "{n}m",
  "time.hours": "{n}h",
  "time.days": "{n}d",

  "nav.inbox": "Inbox",
  "nav.faq": "Help center",
  "nav.reports": "Reports",
  "nav.settings": "Settings",
  "layout.workspace": "Workspace",
  "layout.newWorkspace": "+ New workspace",
  "layout.signOut": "Sign out",

  "app.unreachable.title": "Can't reach the server",
  "app.unreachable.body": "Check your connection and try again.",
  "app.noWorkspaces.title": "No workspaces yet",
  "app.noWorkspaces.body": "Ask an admin to invite you to a workspace.",

  "error.pane": "Couldn't display this conversation.",
  "error.tryAgain": "Try again",
  "error.body": "This screen hit an unexpected error.",
  "error.goInbox": "Go to inbox",

  "login.title": "Sign in to Support",
  "login.sent": "If {email} has access, a sign-in link is on its way. It expires in 15 minutes.",
  "login.intro": "We'll email you a one-time sign-in link.",
  "login.email": "Work email",
  "login.sendFailed": "Couldn't send the link. Try again in a minute.",
  "login.submit": "Email me a link",
  "verify.incomplete": "This link is incomplete.",
  "verify.failed": "Sign-in failed.",
  "verify.expired": "Link expired",
  "verify.signingIn": "Signing you in…",
  "verify.newLink": "Request a new link",

  "newWs.superAdminOnly": "Only super admins can create workspaces.",
  "newWs.created": "Workspace created",
  "newWs.copySecret": "Copy the identity secret now. It's shown only once (you can rotate it later).",
  "newWs.secretLabel": "Identity secret (server only)",
  "newWs.continue": "Continue to setup",
  "newWs.createFailed": "Couldn't create workspace",
  "newWs.title": "New workspace",
  "newWs.intro": "One workspace per app or product. Each has its own help center, inbox and keys.",
  "newWs.submit": "Create workspace",

  "reports.period": "Period",
  "reports.lastDays": "Last {count} days",
  "reports.loadFailed": "Couldn't load the report.",
  "reports.conversations": "Conversations",
  "reports.resolved": "Resolved",
  "reports.medianReply": "Median first reply",
  "reports.satisfaction": "Satisfaction",
  "reports.ratingsCount": "{count} ratings",
  "reports.ratingsCount.one": "{count} rating",
  "reports.ratingsCount.other": "{count} ratings",
  "reports.ratings": "Ratings",
  "reports.barLabel": "{count} ratings of {score}",
  "reports.barLabel.one": "{count} rating of {score}",
  "reports.barLabel.other": "{count} ratings of {score}",
  "reports.recentComments": "Recent comments",
  "reports.noComments": "No comments yet.",

  "status.open": "Open",
  "status.pending": "Pending",
  "status.resolved": "Resolved",

  "inbox.title": "Support Inbox",
  "inbox.conversations": "Conversations",
  "inbox.liveStopped": "Live updates stopped.",
  "inbox.notifyPrompt": "Get a desktop alert for new messages.",
  "inbox.enableNotifications": "Enable notifications",
  "inbox.notNow": "Not now",
  "inbox.statusFilter": "Status",
  "inbox.assignee": "Assignee",
  "inbox.all": "All",
  "inbox.mine": "Mine",
  "inbox.unassigned": "Unassigned",
  "inbox.loadFailed": "Couldn't load conversations.",
  "inbox.empty.open": "No open conversations 🎉",
  "inbox.empty.pending": "No pending conversations 🎉",
  "inbox.empty.resolved": "No resolved conversations 🎉",
  "inbox.you": "You",
  "inbox.agent": "Agent",
  "inbox.attachment": "📎 Attachment",
  "inbox.unreadCount": "{count} unread",
  "inbox.loadMoreFailed": "Couldn't load more conversations",
  "inbox.loadMore": "Load more",
  "inbox.select": "Select a conversation",
  "inbox.visitor": "Visitor {id}",

  "contact.aria": "Contact",
  "contact.verified": "✓ Verified user",
  "contact.anonymous": "Anonymous visitor",
  "contact.userId": "User ID",
  "contact.lastSeen": "Last seen",
  "contact.started": "Started",
  "contact.rating": "Rating",

  "event.resolved": "resolved",
  "event.reopened": "reopened",
  "event.assigned": "assigned",
  "event.csat_request": "rating requested",
  "event.auto_reply": "auto-reply",

  "sys.resolved": "Marked as resolved",
  "sys.reopened": "Reopened",
  "sys.assigned": "Assigned to {name}",
  "sys.csat_request": "Rating requested",
  "sys.auto_reply": "Auto-reply: {body}",

  "thread.aria": "Conversation",
  "thread.verified": "Verified user",
  "thread.you": "{name} (you)",
  "thread.reopen": "Reopen",
  "thread.snooze": "Snooze",
  "thread.snoozeTitle": "Waiting on the customer",
  "thread.resolve": "Resolve",
  "thread.liveStopped": "Live updates stopped for this conversation.",
  "thread.loadFailed": "Couldn't load this conversation.",
  "thread.loadEarlier": "Load earlier",
  "thread.loadEarlierFailed": "Couldn't load earlier messages",
  "thread.failedRetry": "Failed — retry",
  "thread.typing": "{name} is typing…",
  "thread.seen": "Seen",
  "thread.updateFailed": "Update failed",

  "composer.uploadFailed": "Upload failed",
  "composer.savedReplies": "Saved replies",
  "composer.placeholder": "Reply to {name}… (type / for saved replies)",
  "composer.reply": "Reply",
  "composer.attach": "📎 Attach",
  "composer.uploading": "Uploading…",
  "composer.removeFile": "Remove {name}",
  "composer.send": "Send",

  "faq.untitled": "(untitled)",
  "faq.loadFailed": "Couldn't load the help center.",
  "faq.loadFailedToast": "Couldn't load the help center",
  "faq.notFound": "Article not found.",
  "faq.backToList": "Back to the help center",
  "faq.intro": "Articles show in the in-app help center and are suggested before customers start a chat.",
  "faq.newArticle": "New article",
  "faq.categories": "Categories",
  "faq.categoriesIntro": "Group articles into topics. Titles are per language.",
  "faq.addCategoryFailed": "Couldn't add category",
  "faq.newCategoryPlaceholder": "New category title ({locale})",
  "faq.addCategory": "Add category",
  "faq.noArticles": "No articles yet. Start with your most common question.",
  "faq.uncategorized": "Uncategorized",
  "faq.languages": "Languages",
  "faq.views": "Views",
  "faq.helpful": "Helpful",
  "faq.helpfulValue": "{percent}% of {votes}",
  "faq.published": "Published",
  "faq.draft": "Draft",
  "faq.missing": "Missing",
  "faq.categoryTitle": "Title ({locale})",
  "faq.saveCategoryFailed": "Couldn't save category",
  "faq.categorySaved": "Category saved",
  "faq.deleteCategoryConfirm": "Delete this category? Its articles become uncategorized.",
  "faq.deleteCategoryFailed": "Couldn't delete category",
  "faq.addTitleFirst": "Add a title first",
  "faq.deleteArticleConfirm": "Delete this article in all languages?",
  "faq.deleteArticleFailed": "Couldn't delete article",
  "faq.category": "Category",
  "faq.titlePlaceholder": "How do I…?",
  "faq.untitledExisting": "Add a title for {locale}, or remove the {locale} translation below.",
  "faq.untitledNew": "Add a title for {locale}, or clear its body to leave this language out.",
  "faq.slug": "URL slug",
  "faq.slugHint": "Leave empty to generate from the title.",
  "faq.body": "Body (Markdown)",
  "faq.bodyHint": "Supports **bold**, *italic*, lists, links, images and `code`. HTML is shown as text.",
  "faq.publishedIn": "Published in {locale}",
  "faq.removeTranslation": "Remove {locale} translation",
  "faq.preview": "Preview",
  "faq.untitledPreview": "Untitled",

  "settings.tab.general": "General",
  "settings.tab.hours": "Office hours",
  "settings.tab.install": "Install",
  "settings.tab.push": "Push notifications",
  "settings.tab.team": "Team",
  "settings.tab.canned": "Saved replies",
  "settings.adminOnly": "Only workspace admins can change these settings.",
  "settings.loadFailed": "Couldn't load settings.",
  "settings.reloadFailed": "Couldn't reload settings",

  "general.branding": "Branding",
  "general.brandingIntro": "How the help center and chat look inside your app.",
  "general.brandColor": "Brand color",
  "general.brandColorPicker": "Brand color picker",
  "general.brandColorHex": "Brand color hex",
  "general.logoUrl": "Logo URL",
  "general.logoHint": "HTTPS image shown at the top of the help center.",
  "general.languages": "Languages",
  "general.languagesIntro": "The UI follows the device language when it's enabled here; articles fall back to the default language.",
  "general.defaultLanguage": "Default language",
  "general.greeting": "Greeting ({locale})",
  "general.greetingHint": "Leave empty to use the built-in greeting.",
  "general.csatTitle": "Satisfaction ratings",
  "general.csatAsk": "Ask customers to rate the conversation when it's resolved",

  "hours.intro": "Outside these hours customers see that you're away and get your auto-reply.",
  "hours.enable": "Use office hours (otherwise you always show as online)",
  "hours.timezone": "Timezone",
  "hours.open": "Open",
  "hours.closed": "Closed",
  "hours.opens": "{day} opens",
  "hours.closes": "{day} closes",
  "hours.replyTitle": "Reply time & auto-reply",
  "hours.typical": "Typical reply time (minutes)",
  "hours.typicalHint": "Shown to customers while you're online. Leave empty to hide.",
  "hours.away": "Away message ({locale})",
  "hours.awayHint": "Sent automatically when someone writes outside office hours (at most once per 12h per conversation).",

  "install.keys": "Keys",
  "install.publishableKey": "Publishable key",
  "install.publishableHint": "Safe to ship in your app and website.",
  "install.identitySecret": "Identity secret",
  "install.secretHint": "Keep this on your server. Use it to sign user ids so chats are tied to logged-in users.",
  "install.secretHidden": "Hidden. Rotating it immediately invalidates existing user hashes.",
  "install.rotateConfirm": "Rotate the identity secret? Your server must switch to the new secret right away.",
  "install.rotateFailed": "Couldn't rotate the secret",
  "install.rotate": "Rotate secret",
  "install.origins": "Allowed websites",
  "install.originsIntro": "Origins allowed to use the web widget (one per line, e.g. https://app.example.com). Use * to allow any. Mobile apps don't need this.",
  "install.saveOrigins": "Save origins",
  "install.reactWeb": "React (web)",
  "install.anyWebsite": "Any website",
  "install.verifyUsers": "Verify users (server-side)",
  "install.verifyIntro": "Compute the user hash on your server and pass it with the user id.",

  "push.fcmTitle": "Android (Firebase Cloud Messaging)",
  "push.fcmIntro": "Upload a Firebase service account JSON with the Cloud Messaging permission.",
  "push.fcmFile": "Firebase service account JSON",
  "push.saveFcm": "Save FCM credentials",
  "push.apnsTitle": "iOS (Apple Push Notification service)",
  "push.apnsIntro": "Create an APNs auth key (.p8) in your Apple developer account.",
  "push.p8File": ".p8 key file",
  "push.keyId": "Key ID",
  "push.teamId": "Team ID",
  "push.saveApns": "Save APNs credentials",
  "push.configured": "Configured {date}",
  "push.notConfigured": "Not configured",

  "team.invite": "Invite a teammate",
  "team.inviteIntro": "They'll get an email with a sign-in link.",
  "team.role": "Role",
  "team.role.agent": "Agent",
  "team.role.admin": "Admin",
  "team.sendInvite": "Send invite",
  "team.inviteSent": "Invite sent",
  "team.inviteNoEmail": "Added. No email sent: this address got several sign-in emails recently. They can use one of those.",
  "team.inviteFailed": "Invite failed",
  "team.members": "Members",
  "team.loadFailed": "Couldn't load members.",
  "team.loadFailedToast": "Couldn't load members",
  "team.online": "online",
  "team.removeConfirm": "Remove {name}? Their conversations become unassigned.",
  "team.removeFailed": "Couldn't remove member",

  "canned.intro": "Type {shortcut} in the composer to insert one. Use {name} for the customer's first name and {agent} for yours.",
  "canned.shortcut": "Shortcut",
  "canned.shortcutHint": "lowercase letters, numbers, dashes",
  "canned.message": "Message",
  "canned.empty": "No saved replies yet. Create one, then type its /shortcut in the reply box to insert it.",
  "canned.new": "New saved reply",
  "canned.deleteConfirm": "Delete the saved reply /{shortcut}?",
  "canned.deleteFailed": "Couldn't delete saved reply",
  "canned.loadFailed": "Couldn't load saved replies.",
  "canned.loadFailedToast": "Couldn't load saved replies",
};

export type DashKey = keyof typeof en;
export type Catalog = Record<DashKey, string>;
